import { batch, createSignal } from 'solid-js'
import { createStore, produce, reconcile, unwrap } from 'solid-js/store'
import { addCommand, batchCommand, type Command, History, patchCommand, removeCommand, type Selector, snapshot } from './history'
import { MAX_OBJECTS, MAX_SOURCES, newId, type Primitive, type SceneDoc, type Source } from './schema'

type Patch = Record<string, unknown>

export interface Transient {
  /** Apply a patch live without recording history. */
  preview(patch: Patch): void
  /** Record the net change as one history entry. */
  commit(label: string): void
  cancel(): void
}

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T

export function createSceneStore(initial: SceneDoc) {
  const [doc, setDoc] = createStore<SceneDoc>(clone(initial))
  const [canUndo, setCanUndo] = createSignal(false)
  const [canRedo, setCanRedo] = createSignal(false)
  const [undoLabel, setUndoLabel] = createSignal<string | undefined>()
  const [redoLabel, setRedoLabel] = createSignal<string | undefined>()
  /** Bumped on every committed or live mutation; consumers use it for autosave. */
  const [revision, setRevision] = createSignal(0)

  const history = new History(
    (fn) => {
      setDoc(produce(fn))
      setRevision((r) => r + 1)
    },
    () => {
      setCanUndo(history.canUndo)
      setCanRedo(history.canRedo)
      setUndoLabel(history.undoLabel)
      setRedoLabel(history.redoLabel)
    },
  )

  const plain = () => unwrap(doc)

  function patch(sel: Selector, p: Patch, label: string, coalesce = false) {
    const keys = Object.keys(p)
    const before = snapshot(plain(), sel, keys)
    const key = coalesce ? `${JSON.stringify(sel)}:${keys.sort().join(',')}` : undefined
    history.exec(patchCommand(label, sel, before, p, key))
  }

  /** Several patches as one undo step (e.g. resizing the room and pulling sources back inside). */
  function patchMany(items: { sel: Selector; patch: Patch }[], label: string) {
    const cmds = items.map(({ sel, patch: p }) => patchCommand(label, sel, snapshot(plain(), sel, Object.keys(p)), p))
    history.exec(batchCommand(label, cmds))
  }

  function transient(sel: Selector, keys: string[]): Transient {
    const before = snapshot(plain(), sel, keys)
    let last: Patch = before
    return {
      preview(p) {
        last = { ...last, ...p }
        setDoc(produce((d) => patchCommand('', sel, before, p).redo(d)))
        setRevision((r) => r + 1)
      },
      commit(label) {
        const after = snapshot(plain(), sel, keys)
        if (JSON.stringify(after) === JSON.stringify(before)) return
        history.record(patchCommand(label, sel, before, after))
      },
      cancel() {
        setDoc(produce((d) => patchCommand('', sel, before, before).redo(d)))
        setRevision((r) => r + 1)
      },
    }
  }

  function addObject(obj: Primitive): boolean {
    if (doc.objects.length >= MAX_OBJECTS) return false
    history.exec(addCommand('objects', obj))
    return true
  }

  function addSource(src: Source): boolean {
    if (doc.sources.length >= MAX_SOURCES) return false
    history.exec(addCommand('sources', src))
    return true
  }

  function remove(collection: 'objects' | 'sources', id: string) {
    const cmd = removeCommand(plain(), collection, id)
    if (cmd) history.exec(cmd)
  }

  function duplicateObject(id: string): Primitive | null {
    const src = doc.objects.find((o) => o.id === id)
    if (!src || doc.objects.length >= MAX_OBJECTS) return null
    const copy: Primitive = {
      ...clone(unwrap(src)),
      id: newId('obj'),
      name: `${src.name} copy`,
      position: [src.position[0] + 1, src.position[1], src.position[2] + 1],
    }
    history.exec(addCommand('objects', copy))
    return copy
  }

  function duplicateSource(id: string): Source | null {
    const src = doc.sources.find((o) => o.id === id)
    if (!src || doc.sources.length >= MAX_SOURCES) return null
    const copy: Source = {
      ...clone(unwrap(src)),
      id: newId('src'),
      name: `${src.name} copy`,
      position: [src.position[0] + 1, src.position[1], src.position[2] + 1],
      seed: Math.floor(Math.random() * 0xffffffff),
    }
    history.exec(addCommand('sources', copy))
    return copy
  }

  function exec(cmd: Command) {
    history.exec(cmd)
  }

  /** Load a whole new document (new project, open, preset) and reset history. */
  function replace(next: SceneDoc) {
    batch(() => {
      setDoc(reconcile(clone(next)))
      history.clear()
      setRevision((r) => r + 1)
    })
  }

  return {
    doc,
    plain,
    revision,
    canUndo,
    canRedo,
    undoLabel,
    redoLabel,
    patch,
    patchMany,
    transient,
    addObject,
    addSource,
    remove,
    duplicateObject,
    duplicateSource,
    exec,
    batchCommand,
    replace,
    undo: () => history.undo(),
    redo: () => history.redo(),
  }
}

export type SceneStore = ReturnType<typeof createSceneStore>
